import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useTransactions } from "../AnalyzeTransactionsTable/hooks";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";

interface PLSummary {
  initialBalance: number;
  endBalance: number;
  categories: {
    [key: string]: number;
  };
}

export const CashflowChart: React.FC = () => {
  const transactions = useTransactions();

  // Get unique accounts first
  const accounts = React.useMemo(() => {
    const uniqueAccounts = new Set(transactions.map((tx) => tx.account));
    return Array.from(uniqueAccounts);
  }, [transactions]);

  // Initialize selectedAccount as null instead of empty string
  const [selectedAccount, setSelectedAccount] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0]);
    }
  }, [accounts, selectedAccount]);

  // Add date filter state
  const [dateRange, setDateRange] = React.useState({
    startDate: "",
    endDate: "",
  });

  // Update dailyBalances to include date filtering
  const dailyBalances = React.useMemo(() => {
    const balancesByDate = new Map<string, number>();
    let runningBalance = 0;

    // Filter transactions for selected account and date range
    const filteredTransactions = transactions.filter((tx) => {
      const txDate = new Date(tx.date);
      const isInDateRange =
        (!dateRange.startDate || txDate >= new Date(dateRange.startDate)) &&
        (!dateRange.endDate || txDate <= new Date(dateRange.endDate));
      return tx.account === selectedAccount && isInDateRange;
    });

    // Sort transactions by date
    const sortedTransactions = [...filteredTransactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Calculate running balance for each date
    sortedTransactions.forEach((tx) => {
      const date = tx.date;
      runningBalance += Number(tx.amount);
      balancesByDate.set(date, runningBalance);
    });

    // Convert to array format for Recharts
    return Array.from(balancesByDate.entries()).map(([date, balance]) => ({
      date,
      balance,
    }));
  }, [transactions, selectedAccount, dateRange]);

  // Add P&L calculations
  const plSummary = React.useMemo(() => {
    if (!selectedAccount) return null;

    const filteredTransactions = transactions.filter((tx) => {
      const txDate = new Date(tx.date);
      const isInDateRange =
        (!dateRange.startDate || txDate >= new Date(dateRange.startDate)) &&
        (!dateRange.endDate || txDate <= new Date(dateRange.endDate));
      return tx.account === selectedAccount && isInDateRange;
    });

    const sortedTransactions = [...filteredTransactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const summary: PLSummary = {
      initialBalance: 0,
      endBalance: 0,
      categories: {},
    };

    let runningBalance = 0;
    sortedTransactions.forEach((tx, index) => {
      if (index === 0) summary.initialBalance = runningBalance;

      runningBalance += Number(tx.amount);

      // Track amounts by category
      const amount = Number(tx.amount);
      const category = tx.category || "Uncategorized"; // Provide default category
      if (!summary.categories[category]) {
        summary.categories[category] = 0;
      }
      summary.categories[category] += amount;
    });

    summary.endBalance = runningBalance;
    return summary;
  }, [transactions, selectedAccount, dateRange]);

  const handleExportToCSV = () => {
    if (!plSummary) return;

    // Prepare CSV content
    const csvContent = [
      "Balance Summary",
      `Initial Balance,${plSummary.initialBalance}`,
      `End Balance,${plSummary.endBalance}`,
      `Net Change,${plSummary.endBalance - plSummary.initialBalance}`,
      "",
      "Category Breakdown",
      "Category,Amount",
      ...Object.entries(plSummary.categories).map(
        ([category, amount]) => `${category},${amount}`
      ),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `pl_summary_${selectedAccount}.csv`;
    link.click();
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-4">
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Select Account</Form.Label>
                {accounts.length > 0 ? (
                  <Form.Control
                    as="select"
                    value={selectedAccount || ""}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    style={{ width: "auto" }}
                    className="px-3 ml-2"
                  >
                    {accounts.map((account) => (
                      <option key={account} value={account}>
                        {account}
                      </option>
                    ))}
                  </Form.Control>
                ) : (
                  <div>No accounts available</div>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 ml-2">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="px-3"
                />
              </div>

              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 ml-2">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  className="px-3"
                />
              </div>
            </div>
            {plSummary && (
              <Button variant="primary" onClick={handleExportToCSV}>
                Export to CSV
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <h4 className="mb-0">Account Balance Over Time</h4>

          <LineChart
            width={800}
            height={400}
            data={dailyBalances}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="balance"
              stroke="#8884d8"
              name="Balance"
            />
          </LineChart>
        </Col>

        {plSummary && (
          <Col lg={4}>
            <Card className="mb-3">
              <Card.Body>
                <Card.Title>Balance Summary</Card.Title>
                <div>
                  Initial Balance: ${plSummary.initialBalance.toFixed(2)}
                </div>
                <div>End Balance: ${plSummary.endBalance.toFixed(2)}</div>
                <div>
                  Net Change: $
                  {(plSummary.endBalance - plSummary.initialBalance).toFixed(2)}
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <Card.Title>Category Breakdown</Card.Title>
                {Object.entries(plSummary.categories).map(
                  ([category, amount]) => (
                    <div
                      key={category}
                      className="d-flex justify-content-between"
                    >
                      <span>{category}</span>
                      <span
                        className={`text-${amount >= 0 ? "success" : "danger"}`}
                      >
                        ${amount.toFixed(2)}
                      </span>
                    </div>
                  )
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
};
